import { Button, Divider } from '@mui/material';
import { DownloadIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetContractQuery } from '@/apis/extended/contractApi';
import { Badge } from '@/components/ui/badge';
import { getContractPropertyAddress } from '@/utils/contractPropertyAddress';
import Appraisal from './Appraisal';
import Assignment from './Assignment';
import BuyerFinancing from './BuyerFinancing';
import CashOffer from './CashOffer';
import Communication from './Communication';
import MortgageFinancing from './Components/FinancialInspection/MortgageFinancing';
import PartiesContactInfo from './ContactInformation';
import DateTime from './DateTime';
import EarnestMoney from './EarnestMoney';
import FinancialStatus from './FinancialStatus';
import FixturesandProperty from './FixturesandProperty';
import ForeignInvestment from './ForeignInvestment';
import GoverningLaw from './GoverningLaw';
import HomeInspection from './HomeInspection';
import HomeWarranties from './HomeWarranties';
import MaintenanceAndRisk from './MaintenanceAndRisk';
import Mediation from './Mediation';
import NoticeAssesment from './NoticeAssesment';
import PlannedCommunity from './PlannedCommunity';
import PostCloseOccupancy from './PostCloseOccupancy';
import PropertyInfo from './PropertyInfo';
import PurchasePrice from './PurchasePrice';
import Recording from './Recording';
import RecoveryFund from './RecoveryFund';
import Release from './Release';
import Representation from './Representation';
import SellerDisclosure from './SellerDisclosure';
import SettlementAndPossession from './SettlementAndPossession';
import Signature from './signature';
import SpecialClause from './SpecialClause';
import ChooseState from './State/choosestate';
import Taxes from './Taxes';
import Termination from './Termination';
import TitleandSurvey from './TitleandSurvey';
import UnbeatableClause from './UnbeatableClause';
import Zoning from './Zoning';

type ViewContractPageProps = {
  /**
   * Optional override. When the component is rendered inside a deal-room
   * stage panel there's no `/view-contract/:contractId` URL param, so this
   * prop lets the parent pass the contract id directly.
   */
  contractId?: string;
  /** `embedded` trims the page-level chrome so the body fits inside a card. */
  variant?: 'page' | 'embedded';
};

const ViewContractPage = ({
  contractId: contractIdProp,
  variant = 'page',
}: ViewContractPageProps = {}) => {
  const params = useParams();
  const navigate = useNavigate();
  const contractId = contractIdProp ?? params.contractId;
  if (!contractId) {
    return <div>No contract ID found</div>;
  }

  const { data: contract } = useGetContractQuery(contractId);

  const settlementDate = contract?.offerSpecifics.closingDate;
  const buyerName = contract?.buyerInfo?.name.toUpperCase();
  const sellerName = contract?.sellerInfo?.name.toUpperCase();

  return (
    <div
      className={
        variant === 'embedded'
          ? 'bg-transparent py-2'
          : 'min-h-screen bg-gray-50 py-8'
      }
    >
      {/* Download Button */}
      <div className="flex justify-end mb-6 mr-10">
        <Button
          onClick={() => {
            navigate(`/contract/download/${contractId}`);
          }}
          className="bg-[#043a93] hover:bg-[#032a6b] text-white px-6 py-3 rounded-lg shadow-md"
        >
          <DownloadIcon className="w-5 h-5 mr-2" />
          PDF Contract For Download
        </Button>
      </div>
      <div className="max-w-4xl mx-auto">
        {/* A4 Paper Container */}
        <div
          id="contract-content"
          className="contract-pdf shadow-2xl mx-auto"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '20mm',
            boxSizing: 'border-box',
            fontFamily: 'Times New Roman, serif',
            fontSize: '12pt',
            lineHeight: '1.5',
            color: '#000',
          }}
        >
          {/* Header */}
          <div className="flex justify-center">
            <Badge
              className="capitalize text-sm bg-gray-500 text-white"
              variant="outline"
            >
              {contract?.contractStatus} Contract
            </Badge>
          </div>
          <div className="text-center mb-8">
            <h1
              className="text-2xl font-bold mb-2"
              style={{ fontSize: '18pt', fontWeight: 'bold' }}
            >
              REAL ESTATE PURCHASE CONTRACT
            </h1>
            <p className="text-sm text-gray-600 mb-4">
              Contract ID: {contract?._id}
            </p>
            <Divider className="my-4" />
          </div>

          {/* Contract Body */}
          <div className="space-y-6">
            {/* Introduction */}
            <div className="mb-6">
              <h2 className="text-xl font-bold uppercase mt-8 mb-2 border-b pb-1">
                PARTIES
              </h2>
              <p className="mb-4"></p>
              <strong>1.</strong> This Real Estate Purchase Contract ("Contract"
              or "Agreement") is made and entered into by and between{' '}
              <span className="font-semibold underline">{buyerName}</span>{' '}
              ("Buyer") and{' '}
              <span className="font-semibold underline">
                {sellerName ? sellerName : 'Seller'}
              </span>{' '}
              ("Seller").
              <p className="mb-4">
                <strong>2.</strong> The parties are referred to as Buyer and
                Seller throughout this Contract.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-6">
              <PartiesContactInfo
                buyerEmail={contract?.buyerInfo?.email ?? 'N/A'}
                buyerPhone={contract?.buyerInfo?.phone ?? 'N/A'}
                buyer2Email={(contract?.buyerInfo as any)?.email2}
                buyer2Phone={(contract?.buyerInfo as any)?.phone2}
                sellerEmail={contract?.sellerInfo?.email ?? 'N/A'}
                sellerPhone={contract?.sellerInfo?.phone ?? 'N/A'}
                buyerAddress={{
                  street: contract?.buyerInfo?.street,
                  city: contract?.buyerInfo?.city,
                  state: contract?.buyerInfo?.state,
                  zip: contract?.buyerInfo?.zip,
                }}
                sellerAddress={{
                  street: contract?.sellerInfo?.street,
                  city: contract?.sellerInfo?.city,
                  state: contract?.sellerInfo?.state,
                  zip: contract?.sellerInfo?.zip,
                }}
              />
            </div>

            {/* Property Information */}
            <div className="mb-6">
              {(() => {
                const prop = getContractPropertyAddress(contract as any);
                return (
                  <PropertyInfo
                    sellerName={contract?.sellerInfo?.name}
                    buyerName={contract?.buyerInfo?.name}
                    propertyAddress={prop.addressLine}
                    propertyCity={prop.city}
                    propertyState={prop.state}
                    propertyZipCode={prop.zip}
                    propertyCounty={
                      contract?.sellerPropertyInfo?.propertyCounty ||
                      contract?.agentSellerInfo?.propertyCounty
                    }
                    propertyCondition={
                      contract?.offerSpecifics?.propertyCondition
                    }
                    legalDescription={
                      contract?.agentSellerInfo?.legalDescription
                    }
                  />
                );
              })()}
            </div>

            {/* Purchase Price */}
            <div className="mb-6">
              <PurchasePrice offerPrice={contract?.agentOfferInfo.offerPrice} />
            </div>

            {/* Settlement and Possession */}
            <div className="mb-6">
              <SettlementAndPossession
                settlementDate={settlementDate}
                buyerName={buyerName}
                sellerName={sellerName}
                financingCommitmentDate={
                  contract?.offerSpecifics?.financingCommittmentDate
                }
                taxDate={contract?.sellerPropertyInfo?.taxDate}
                mortgageCompany={contract?.sellerPropertyInfo?.mortgageCompany}
                escrowAgent={contract?.sellerPropertyInfo?.escrowAgent}
              />
            </div>

            {/* DateTime */}
            <div className="mb-6">
              <DateTime
                offerGoodUntillDate={
                  contract?.timeline?.offerGoodUntillDate || ''
                }
              />
            </div>

            {/* Zoning */}
            <div className="mb-6">
              <Zoning zoning={contract?.agentSellerInfo?.zoning || ''} />
            </div>

            {/* Fixtures and Property */}
            <div className="mb-6">
              <FixturesandProperty
                personalProperty={
                  contract?.offerSpecifics.personalProperty || ''
                }
                leasedProperty={contract?.offerSpecifics.leasedProperty || ''}
                excludedPersonalItem={
                  contract?.offerSpecifics.excludedPersonalItem || ''
                }
              />
            </div>

            {/* Title and Survey */}
            <div className="mb-6">
              <TitleandSurvey
                daysToSelectTitleCompany={
                  contract?.thirdPartyTitleSettlement?.daysToSelectTitleCompany
                }
                buyerName={buyerName}
                sellerName={sellerName}
              />
            </div>

            {/* Notice Assessment */}
            <div className="mb-6">
              <NoticeAssesment
                buyerName={buyerName}
                contract={contract}
                homeinspectionNegotiationDays={
                  contract?.contingency?.homeinspectionNegotiationDays
                }
              />
            </div>

            {/* Appraisal */}
            <div className="mb-6">
              {contract?.contingency?.appraisalContingency ? (
                <Appraisal
                  appraisalContingency={
                    contract?.contingency?.appraisalContingency
                  }
                  daysFromExecutionDate={
                    contract?.contingency?.daysFromExecutionDate
                  }
                  daysForAppraisalNegotiation={
                    contract?.contingency?.daysForAppraisalNegotiation
                  }
                />
              ) : (
                <div className="mb-4">
                  <p>
                    <strong>Appraisal Contingency:</strong>
                  </p>
                  <p className="ml-4">
                    This Contract is not contingent upon the Property appraising
                    for at least the Purchase Price.
                  </p>
                </div>
              )}
            </div>

            {/* Home Inspection */}
            <div className="mb-6">
              <HomeInspection
                homeinspectionNegotiationDays={
                  contract?.contingency?.homeinspectionNegotiationDays
                }
                inspectionContingency={
                  contract?.contingency?.inspectionContingency as any
                }
                inspectionType={contract?.contingency?.inspectionType as any}
                additionalInspectionType={
                  contract?.contingency?.additionalInspectionType
                }
                copyOfInspection={
                  contract?.thirdPartyInspection?.copyOfInspection as any
                }
                presenceOption={
                  contract?.thirdPartyInspection?.presenceOption as any
                }
                enableBuyerNegotiateReleaseDays={
                  contract?.thirdPartyInspection
                    ?.enableBuyerNegotiateReleaseDays
                }
                buyerNegotiateReleaseDays={
                  contract?.thirdPartyInspection?.buyerNegotiateReleaseDays
                }
                enableBuyerNegotiateCreditsPriceDays={
                  contract?.thirdPartyInspection
                    ?.enableBuyerNegotiateCreditsPriceDays
                }
                buyerNegotiateCreditsPriceDays={
                  contract?.thirdPartyInspection?.buyerNegotiateCreditsPriceDays
                }
                enableBuyerNegotiateCreditsCostsDays={
                  contract?.thirdPartyInspection
                    ?.enableBuyerNegotiateCreditsCostsDays
                }
                buyerNegotiateCreditsCostsDays={
                  contract?.thirdPartyInspection?.buyerNegotiateCreditsCostsDays
                }
              />
            </div>

            {/* Cash Offer */}
            <div className="mb-6">
              {contract?.offerSpecifics?.offerType === 'Financed' ? (
                <>
                  <BuyerFinancing />
                  <MortgageFinancing
                    financingCommittmentDate={
                      contract?.offerSpecifics?.financingCommittmentDate
                    }
                    financingLoanProgram={
                      contract?.offerSpecifics?.financingLoanProgram
                    }
                    loanTerm={contract?.offerSpecifics?.loanTerm}
                    loanAmount={contract?.offerSpecifics?.loanAmount}
                    marketInterestRate={
                      contract?.offerSpecifics?.marketInterestRate
                    }
                    lenderName={contract?.offerSpecifics?.lenderName}
                    lenderAddress={contract?.offerSpecifics?.lenderAddress}
                    lenderNMLS={contract?.offerSpecifics?.lenderNMLS}
                    loanOfficerName={contract?.offerSpecifics?.loanOfficerName}
                    loanOfficerPhone={
                      contract?.offerSpecifics?.loanOfficerPhone
                    }
                    loanOfficerEmail={
                      contract?.offerSpecifics?.loanOfficerEmail
                    }
                    loanOfficerNMLS={contract?.offerSpecifics?.loanOfficerNMLS}
                  />
                </>
              ) : (
                <CashOffer />
              )}
            </div>

            {/* Financial Status */}
            <div className="mb-6">
              <FinancialStatus />
            </div>

            {/* Earnest Money */}
            <div className="mb-6">
              <EarnestMoney
                deposit1={contract?.offerSpecifics?.deposit1 ?? 0}
                deposit1Due={contract?.offerSpecifics?.deposit1Due ?? 'N/A'}
                titleCompany={
                  contract?.thirdPartyTitleSettlement?.titleCompany ?? 'N/A'
                }
                {...(contract?.offerSpecifics?.deposit2 && {
                  deposit2: contract.offerSpecifics.deposit2 ?? 'N/A',
                  deposit2Due: contract.offerSpecifics.deposit2Due ?? 'N/A',
                })}
              />
            </div>

            {/* Post-Close Occupancy */}
            <div className="mb-6">
              {contract?.contingency?.postCloseOccupancyDays && (
                <PostCloseOccupancy
                  postCloseOccupancyDays={
                    contract?.contingency?.postCloseOccupancyDays
                  }
                  postCloseOccupancyRent={
                    contract?.contingency?.postCloseOccupancyRent
                  }
                  postCloseOccupancyDeposit={
                    contract?.contingency?.postCloseOccupancyDeposit
                  }
                  postCloseOccupancyTerms={
                    contract?.contingency?.postCloseOccupancyTerms
                  }
                />
              )}
            </div>

            {/* Planned Community */}
            <div className="mb-6">
              <PlannedCommunity
                negotiationDaysFromDiscovery={(() => {
                  const raw =
                    contract?.thirdPartyInspection
                      ?.negotiationDaysFromDiscovery;
                  const n = Number(raw);
                  return Number.isFinite(n) && n > 0 ? n : undefined;
                })()}
              />
            </div>

            {/* Taxes */}
            <div className="mb-6">
              <Taxes contract={contract} />
            </div>

            {/* Maintenance and Risk */}
            <div className="mb-6">
              <MaintenanceAndRisk
                homeinspectionNegotiationDays={
                  contract?.contingency?.homeinspectionNegotiationDays ?? 0
                }
              />
            </div>

            {/* Home Warranties */}
            <div className="mb-6">
              <HomeWarranties />
            </div>

            {/* Recording */}
            <div className="mb-6">
              <Recording />
            </div>

            {/* Assignment */}
            <div className="mb-6">
              <Assignment />
            </div>

            {/* Governing Law */}
            <div className="mb-6">
              {contract && <GoverningLaw contract={contract} />}
            </div>

            {/* Foreign Investment */}
            <div className="mb-6">
              <ForeignInvestment />
            </div>

            {/* Unbeatable Clause */}
            <div className="mb-6">
              <UnbeatableClause
                sellerAssistanceTypes={
                  contract?.offerSpecifics?.sellerAssistanceType
                }
                buyerAgentCompensation={
                  contract?.offerSpecifics.buyerAgentCompensation
                }
                offerPrice={contract?.offerSpecifics?.offerPrice}
                sellerAssistanceClosingCosts={
                  contract?.offerSpecifics?.sellerAssistanceClosingCosts
                }
              />
            </div>

            {/* State Specific Terms */}
            <div className="mb-6">
              <ChooseState
                state={contract?.agentSellerInfo?.propertyState || ''}
              />
            </div>

            {/* Seller Disclosure */}
            <div className="mb-6">
              <SellerDisclosure />
            </div>

            {/* Termination */}
            <div className="mb-6">
              <Termination
                propertyState={contract?.agentSellerInfo?.propertyState}
              />
            </div>

            {/* Mediation */}
            <div className="mb-6">
              <Mediation />
            </div>

            {/* Release */}
            <div className="mb-6">
              <Release />
            </div>

            {/* Recovery Fund */}
            <div className="mb-6">
              <RecoveryFund contract={contract} />
            </div>

            {/* Communication */}
            <div className="mb-6">
              <Communication />
            </div>

            {/* Representation */}
            <div className="mb-6">
              <Representation
                agentEmail={contract?.agentInfo?.sellerAgentEmail ?? 'N/A'}
                agentPhone={
                  contract?.agentInfo?.sellerPhoneNumber?.toString() ?? 'N/A'
                }
                agentName={contract?.agentInfo?.name}
                agentLicenseNumber={contract?.agentInfo?.agentLicenseNumber}
                agentCompanyName={contract?.agentInfo?.agentCompanyName}
                agentCompanyLicense={contract?.agentInfo?.agentCompanyLicense}
                agentCompanyStateLicense={
                  contract?.agentInfo?.agentCompanyStateLicense
                }
                agentCompanyAddress={contract?.agentInfo?.agentCompanyAddress}
                agentAddress={{
                  street: contract?.agentInfo?.street,
                  city: contract?.agentInfo?.city,
                  state: contract?.agentInfo?.state,
                  zip: contract?.agentInfo?.zip,
                }}
              />
            </div>

            {/* Special Clause */}
            <div className="mb-6">
              <SpecialClause />
            </div>

            {/* Signature */}
            <div className="mb-6">
              <Signature
                buyerSignature={contract?.buyerSign?.signature}
                BuyersignedAt={contract?.buyerSign?.signedAt}
                sellerSignature={contract?.sellerSign?.signature}
                SellersignedAt={contract?.sellerSign?.signedAt}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewContractPage;
